<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, Select, SliderV2, Text, Toggle } from '@Shelly/shelly-design-system';
	import { getCameraConfig, saveCameraConfig } from '../../helpers/camera-settings-helpers';
	import { hasCameraConfigKey } from '../../helpers/camera-features';
	import type { CameraNightVisionConfig, NightVisionMode } from '../../types/camera-settings';

	export let device: Device;

	type NightVisionState = {
		mode: NightVisionMode;
		ir_leds: boolean;
		light_threshold: number;
		sensitivity: number;
	};

	const DEFAULTS: NightVisionState = {
		mode: 'auto',
		ir_leds: true,
		light_threshold: 50,
		sensitivity: 50,
	};

	const AUTOSAVE_DELAY = 500;

	/**
	 * One evenly spaced stop inside each 0-19 / 20-39 / 40-59 / 60-79 / 80-100 band the
	 * firmware web UI names.
	 */
	const LIGHT_THRESHOLD_MARKERS = [
		{
			value: 0,
			displayValue: $format('camera_nv_level_very_dark', { default: 'Very dark' }),
			showLabel: true,
		},
		{
			value: 25,
			displayValue: $format('camera_nv_level_dark', { default: 'Dark' }),
			showLabel: false,
		},
		{
			value: 50,
			displayValue: $format('camera_nv_medium', { default: 'Medium' }),
			showLabel: true,
		},
		{
			value: 75,
			displayValue: $format('camera_nv_level_bright', { default: 'Bright' }),
			showLabel: false,
		},
		{
			value: 100,
			displayValue: $format('camera_nv_level_very_bright', { default: 'Very bright' }),
			showLabel: true,
		},
	];

	/**
	 * One evenly spaced stop inside each 0-33 / 34-66 / 67-100 band the firmware reports,
	 * captioned by direction the way the firmware web UI labels this slider.
	 */
	const SENSITIVITY_MARKERS = [
		{
			value: 0,
			displayValue: $format('camera_nv_more_stable', { default: 'More stable' }),
			showLabel: true,
		},
		{
			value: 50,
			displayValue: $format('camera_nv_balanced', { default: 'Balanced' }),
			showLabel: true,
		},
		{
			value: 100,
			displayValue: $format('camera_nv_more_sensitive', { default: 'More sensitive' }),
			showLabel: true,
		},
	];

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const storedNightVision = getCameraConfig(device)?.night_vision;

	const supports = {
		mode: hasCameraConfigKey(device, 'night_vision.mode'),
		ir_leds: hasCameraConfigKey(device, 'night_vision.ir_leds'),
		light_threshold: hasCameraConfigKey(device, 'night_vision.light_threshold'),
		// Pre-release firmware reported a 'low' | 'medium' | 'high' string here rather than 0-100.
		sensitivity:
			hasCameraConfigKey(device, 'night_vision.sensitivity') &&
			typeof storedNightVision?.sensitivity !== 'string',
	};

	const readNightVision = (): NightVisionState => {
		const nv = getCameraConfig(device)?.night_vision;
		return {
			mode: nv?.mode ?? DEFAULTS.mode,
			ir_leds: typeof nv?.ir_leds === 'boolean' ? nv.ir_leds : DEFAULTS.ir_leds,
			light_threshold:
				typeof nv?.light_threshold === 'number' ? nv.light_threshold : DEFAULTS.light_threshold,
			sensitivity: typeof nv?.sensitivity === 'number' ? nv.sensitivity : DEFAULTS.sensitivity,
		};
	};

	let state: NightVisionState = readNightVision();
	let cache: NightVisionState = { ...state };
	let loading = false;
	let saveTimeout: ReturnType<typeof setTimeout> | undefined;

	$: modeOptions = [
		{ title: $format('camera_nv_mode_auto', { default: 'Auto' }), value: 'auto' },
		{ title: $format('camera_nv_mode_day', { default: 'Day' }), value: 'day' },
		{ title: $format('camera_nv_mode_night', { default: 'Night' }), value: 'night' },
	];

	// The light level and sensitivity only drive the automatic day/night switch.
	$: showThresholds = state.mode === 'auto';

	$: hasChanges = (Object.keys(state) as (keyof NightVisionState)[]).some(
		(key) => state[key] !== cache[key],
	);

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: resetToDefaults,
				loading,
				disabled: loading,
				label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const getSelectValue = (e: CustomEvent): any => {
		const d = e.detail;
		if (typeof d === 'string' || typeof d === 'number') return d;
		return d?.value ?? d;
	};

	const scheduleSave = () => {
		if (saveTimeout) clearTimeout(saveTimeout);
		saveTimeout = setTimeout(save, AUTOSAVE_DELAY);
	};

	/** Only sends the fields this firmware actually exposes. */
	const buildConfig = (): CameraNightVisionConfig => {
		const config: CameraNightVisionConfig = {};
		if (supports.mode) config.mode = state.mode;
		if (supports.ir_leds) config.ir_leds = state.ir_leds;
		if (supports.light_threshold) config.light_threshold = state.light_threshold;
		if (supports.sensitivity) config.sensitivity = state.sensitivity;
		return config;
	};

	const save = async () => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = undefined;
		}
		if (!hasChanges) return;
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ night_vision: buildConfig() },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cache = { ...state };
		} else {
			state = { ...cache };
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ night_vision: null },
			$format('camera_nv_reset_success', { default: 'Night vision settings reset to defaults.' }),
		);
		if (success) {
			state = readNightVision();
			cache = { ...state };
		}
		loading = false;
	};

	onDestroy(() => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			save();
		}
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType="h2" class="mb-s">
	{$format('label_night_vision', { default: 'Night vision' })}
</Text>

{#if supports.mode}
	<Select
		size="m"
		width="full"
		icon="fas fa-moon"
		label={$format('camera_nv_mode_label', { default: 'Night vision mode' })}
		options={modeOptions}
		value={modeOptions.find((o) => o.value === state.mode)}
		on:change={(e) => {
			state = { ...state, mode: getSelectValue(e) };
			scheduleSave();
		}}
		disabled={loading}
		id="camera-nv-mode"
		aria-label={$format('camera_nv_mode_label', { default: 'Night vision mode' })}
	/>
	<Text styleType="body-extra-small" class="text-color-muted mt-xs d-block">
		{$format('camera_nv_mode_hint', {
			default: 'Auto switches between day and night based on the ambient light level.',
		})}
	</Text>
{/if}

{#if supports.ir_leds}
	<Toggle
		size="m"
		class="mt-m"
		checked={state.ir_leds}
		disabled={loading}
		on:click={() => {
			state = { ...state, ir_leds: !state.ir_leds };
			scheduleSave();
		}}
		info={$format('camera_nv_ir_leds_hint', {
			default: 'Lights the scene with infrared LEDs so the camera can see in complete darkness.',
		})}
		id="camera-nv-ir-leds"
	>
		{$format('camera_nv_ir_leds', { default: 'Enhanced night vision (IR LED)' })}
	</Toggle>
{/if}

{#if showThresholds && (supports.light_threshold || supports.sensitivity)}
	<Text styleType="h2" class="mt-l mb-s">
		{$format('camera_nv_auto_switching', { default: 'Automatic switching' })}
	</Text>
{/if}

{#if supports.light_threshold && showThresholds}
	<SliderV2
		id="camera-nv-light-threshold"
		value={state.light_threshold}
		on:value_change={(e) => {
			state = { ...state, light_threshold: e.detail.value };
			scheduleSave();
		}}
		disabled={loading}
		min={0}
		max={100}
		step={1}
		type="incremented"
		title="{$format('camera_nv_light_threshold', { default: 'Switch to night mode' })}:"
		titleTypography="body-small"
		a11yProps={{
			ariaLabel: $format('camera_nv_light_threshold', { default: 'Switch to night mode' }),
			ariaLabelValue: String(state.light_threshold),
		}}
		lineMarkers={LIGHT_THRESHOLD_MARKERS}
	/>
	<Text styleType="body-extra-small" class="text-color-muted mt-xs d-block">
		{$format('camera_nv_light_threshold_hint', {
			default:
				'The higher the value, the sooner the camera switches to night mode as light fades.',
		})}
	</Text>
{/if}

{#if supports.sensitivity && showThresholds}
	<SliderV2
		id="camera-nv-sensitivity"
		class="mt-m"
		value={state.sensitivity}
		on:value_change={(e) => {
			state = { ...state, sensitivity: e.detail.value };
			scheduleSave();
		}}
		disabled={loading}
		min={0}
		max={100}
		step={1}
		type="incremented"
		title="{$format('camera_nv_switching_sensitivity', { default: 'Switching sensitivity' })}:"
		titleTypography="body-small"
		a11yProps={{
			ariaLabel: $format('camera_nv_sensitivity_value', { default: 'Sensitivity value' }),
			ariaLabelValue: String(state.sensitivity),
		}}
		lineMarkers={SENSITIVITY_MARKERS}
	/>
	<Text styleType="body-extra-small" class="text-color-muted mt-xs d-block">
		{$format('camera_nv_switching_sensitivity_hint', {
			default:
				'How much the light has to change before the camera switches again. Lower values avoid switching back and forth.',
		})}
	</Text>
{/if}

{#if !$showNewLayout}
	<Button
		class="mt-m"
		variant="secondary"
		width="full"
		size="m"
		{loading}
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
{/if}
